import React, { useEffect, useState } from 'react';
import getCreditAppeal from '../../api/deals/getCreditAppeal';
import selectCreditAppeal from '../../api/creditPlatform/selectCreditAppeal';
import Button from '../Button/Button';
import { BankOffersSkeleton } from './CreditSkeletons';
import formatNumber from '../../utils/formatNumber';
import alert from '../../helpers/alert';
import { BANK_CREDIT_ACCEPTED, BANK_CREDIT_REJECTED, BANK_CREDIT_STATUSES } from '../../constants/bankCreditStatuses';
import { DEAL_STATUS_TTL_EXPIRED } from '../../constants/dealStatuses';
import styles from '../../pages/Deal/deal.module.css';

/**
 * Ответы банков по сделке — для раздела "Лиды" (менеджер выбирает оффер за
 * клиента). Упрощённая версия карточек из DealCredits.jsx: там всё завязано на
 * степ-машину сделки (generateCheck, DEAL_STEPS, StepControlButtons), которая
 * тут не нужна — просто список офферов и выбор. Замена машины — кнопкой в блоке
 * автомобиля выше (LeadDealInfo), одна на сделку.
 */
const LeadBankOffers = ({ chunkId, dealStatusId, onOfferSelected }) => {
  const [appeals, setAppeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [choosing, setChoosing] = useState(null);

  const isExpired = dealStatusId === DEAL_STATUS_TTL_EXPIRED;

  const init = () => {
    if (!chunkId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setLoadError(false);
    getCreditAppeal({ chunkId })
      .then((res) => setAppeals(res.data?.appeals || []))
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    init();
  }, [chunkId]);

  const chooseBank = async (appeal) => {
    setChoosing(appeal.id);
    try {
      await selectCreditAppeal({ appealId: appeal.id, offerType: appeal.offerType });
      init();
      onOfferSelected?.();
    } catch (e) {
      alert.error(e?.response?.data?.errorMessage || 'Ошибка при выборе банка');
    } finally {
      setChoosing(null);
    }
  };

  if (loading) return <BankOffersSkeleton />;

  if (!chunkId) {
    return <p>Сделка ещё не создана — заявки в банки ещё не отправлялись.</p>;
  }

  if (loadError) {
    return (
      <div>
        <p style={{ color: '#EE7171', marginBottom: 10 }}>Не удалось загрузить заявки в банки.</p>
        <Button text="Повторить" onClick={init} />
      </div>
    );
  }

  return (
    <div>
      {isExpired && (
        <div style={{ background: '#FDEDED', border: '1px solid #EE7171', borderRadius: 8, padding: '10px 16px', marginBottom: 16 }}>
          Банки не ответили в срок. Чтобы выбрать предложение, подберите другой автомобиль выше.
        </div>
      )}

      {appeals.length === 0 ? (
        <p>Заявок в банки по этой сделке пока нет.</p>
      ) : (
        appeals.map((item) => (
          <div className={styles.bank} key={item.id} style={isExpired ? { opacity: 0.6 } : undefined}>
            <div className={styles.bank__row}>
              <div className={styles.bank__img}>
                <img src={item?.bank?.logo} alt={item?.bank?.name} />
              </div>
              <p className={styles.bank_name}>{item?.bank?.name}</p>
              <p
                className={`${styles.status} ${
                  item?.statusId === BANK_CREDIT_ACCEPTED ? styles.status__accepted : ''
                } ${item?.statusId === BANK_CREDIT_REJECTED ? styles.status__rejected : ''}`}
              >
                {BANK_CREDIT_STATUSES[item?.statusId]}
              </p>
            </div>
            {item?.errorMessage && (
              <div style={{ padding: '0 20px', marginBottom: 12 }}>
                <p className={styles.sub} style={{ color: '#EE7171', wordBreak: 'break-all' }}>
                  {item.errorMessage}
                </p>
              </div>
            )}
            <div className={styles.bank__info} style={{ padding: '0 20px' }}>
              {item?.loan_amount ? (
                <div className={styles.bank__info_row}>
                  <p className={styles.bank__info_title}>Сумма займа</p>
                  <p className={styles.bank__info_sub}>{formatNumber(item.loan_amount)}</p>
                </div>
              ) : null}
              <div className={styles.bank__info_row}>
                <p className={styles.bank__info_title}>Первоначальный взнос</p>
                <p className={styles.bank__info_sub}>{formatNumber(item?.initialFee)}</p>
              </div>
              <div className={styles.bank__info_row}>
                <p className={styles.bank__info_title}>Срок кредитования</p>
                <p className={styles.bank__info_sub}>{item?.period} м.</p>
              </div>
              <div className={styles.bank__info_row}>
                <p className={styles.bank__info_title}>Процентная ставка</p>
                <p className={styles.bank__info_sub}>{item?.percent}%</p>
              </div>
              <div className={styles.bank__info_row}>
                <p className={styles.bank__info_title}>Ежемесячный платёж</p>
                <p className={styles.bank__info_sub}>{formatNumber(item?.monthlyPayment)}</p>
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '0 20px 20px' }}>
              <Button
                text="Выбрать предложение"
                fill
                disabled={choosing === item.id || isExpired}
                onClick={() => chooseBank(item)}
              />
            </div>
          </div>
        ))
      )}
    </div>
  );
};

export default LeadBankOffers;
